import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { base_url } from "../../utils/utils";
import moment from "moment";

const userIdString = localStorage.getItem("userId");
const userId = JSON.parse(userIdString)?.aid;

// Profile Data
export const get_profile_data = createAsyncThunk("profile", async (id) => {
  const body = new FormData();
  body.append("action", "profile");
  body.append("agencyid", id);
  const response = await fetch(base_url + "profile.php", {
    method: "POST",
    body: body,
  });
  const res = await response.json();
  return res;
});

// Employee Profile Data
export const get_emp_profile_data = createAsyncThunk(
  "emp_profile",
  async ({ user_id, emp_id }) => {
    const body = new FormData();
    body.append("action", "employeeprofile");
    body.append("agencyid", user_id);
    body.append("emp_code", emp_id);
    const response = await fetch(base_url + "profile.php", {
      method: "POST",
      body: body,
    });
    const res = await response.json();
    return res;
  },
);

// Home Data
export const get_home_data = createAsyncThunk("home", async (id) => {
  const userIdString = localStorage.getItem("userId");
  const userId = JSON.parse(userIdString)?.aid;
  const empid = JSON.parse(userIdString)?.eid;
  const body = new FormData();
  body.append("agencyid", id || userId);
  if (empid) {
    body.append("emp_code", empid);
  }
  const response = await fetch(base_url + "home.php", {
    method: "POST",
    body: body,
  });
  const res = await response.json();
  return res;
});

// Async Thunk for fetching dashboard data
export const get_dashboard_data = createAsyncThunk(
  "dashboard/get_dashboard_data",
  async (data = {}, { rejectWithValue }) => {
    try {
      const userIdString = localStorage.getItem("userId");

      if (!userIdString) {
        throw new Error("User ID not found in localStorage");
      }

      const userData = JSON.parse(userIdString);
      const userId = userData?.aid;

      if (!userId) {
        throw new Error("Agency ID not found");
      }
      const month = new Date().toLocaleString("en-US", {
        month: "2-digit",
      });
      const body = new FormData();
      body.append("agencyid", userId);

      // 🔥 ALWAYS APPEND ALL FILTER KEYS
      body.append("fy", data.fy || "");
      body.append("date", data.date || "");
      body.append("month", data.month || month);
      body.append("week", data.week || "");
      body.append("quarter", data.quarter || "");

      const response = await fetch(base_url + "dashboard.php", {
        method: "POST",
        body: body,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();

      console.log("📡 Dashboard API Request Payload", {
        fy: data.fy || "",
        date: data.date || "",
        month: data.month || "",
        week: data.week || "",
        quarter: data.quarter || "",
      });

      if (!res.success) {
        return rejectWithValue(res.message || "Failed to fetch dashboard data");
      }

      return res;
    } catch (error) {
      console.error("Dashboard API Error:", error);
      return rejectWithValue(
        error.message || "An error occurred while fetching dashboard data",
      );
    }
  },
);

const createDashboardChartThunk = (type, actionName) =>
  createAsyncThunk(type, async (data = {}, { rejectWithValue }) => {
    try {
      const userIdString = localStorage.getItem("userId");
      const userId = JSON.parse(userIdString)?.aid;

      const body = new FormData();
      body.append("agencyid", userId);
      body.append("action", actionName);

      body.append("fy", data.fy || "");
      body.append("date", data.date || "");
      body.append("month", data.month || "");
      body.append("week", data.week || "");
      body.append("quarter", data.quarter || "");

      const response = await fetch(base_url + "dashboard.php", {
        method: "POST",
        body,
      });

      const res = await response.json();

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res.data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  });

export const get_purchase_sales = createDashboardChartThunk(
  "dashboard/purchase_sales",
  "purchase_sales",
);

export const get_ncdbcrctv = createDashboardChartThunk(
  "dashboard/ncdbcrctv",
  "ncdbcrctv",
);

export const get_sales = createDashboardChartThunk("dashboard/sales", "sales");
export const get_cashcredit = createDashboardChartThunk(
  "dashboard/cashcredit",
  "revenue",
);

export const get_purchase_sales_expenses = createDashboardChartThunk(
  "dashboard/purchase_sales_expenses",
  "purchase_sales_expenses",
);

export const get_profile_name = createAsyncThunk("firmname", async () => {
  return JSON.parse(localStorage.getItem("userId"))?.Name?.toUpperCase();
});
export const get_profile_img_url = createAsyncThunk("image", async () => {
  return JSON.parse(localStorage.getItem("userId"))?.image_url;
});
export const get_profile_pin = createAsyncThunk("pin", async () => {
  return JSON.parse(localStorage.getItem("userId"))?.PIN;
});
export const get_pin_data = createAsyncThunk("pin", async (pin) => {
  return pin;
});

const initialState = {
  profile_data: {},
  emp_profile_data: {},
  emp_data: {},
  profile_loading: false,
  showroom_address_list: [],
  godown_address_list: [],
  img_url: "",
  emp_img_url: "",
  firm_name: "",
  contact_person_name: "",
  pin_data: "",
  emp_pin_data: "",
  home_data: {},
  dashboard_data: null,
  dashboard_loading: false,
  dashboard_error: null,
  purchase_sales_data: [],
  ncdbcrctv_data: [],
  sales_data: [],
  cashcredit_data: [],
  purchase_sales_expenses_data: [],
  day_end_date: "",
  seal_image: "",
  signature_image: "",
  upi_qr_image: "",
  upi_id: "",
  alert_count: "0",
  user_turnover: "",
  token_status: 0,
  chart_loading: {
    purchase_sales: false,
    ncdbcrctv: false,
    sales: false,
    cashcredit: false,
    purchase_sales_expenses: false,
  },
};

const profileSlice = createSlice({
  name: "profile",
  initialState: initialState,
  extraReducers: (builder) => {
    builder.addCase(get_profile_data.fulfilled, (state, action) => {
      const data = action.payload;
      state.profile_loading = false;
      if (data.success) {
        state.profile_data = data?.list[0];
        state.showroom_address_list =
          data?.list?.addresslist?.filter((v) => v?.ADDRESS_TYPE == "1") || [];
        state.godown_address_list =
          data?.list?.addresslist?.filter((v) => v?.ADDRESS_TYPE == "2") || [];
        state.img_url = data?.list[0]?.image_url;
        state.seal_image = data?.list[0]?.seal_url;
        state.signature_image = data?.list[0]?.sign_url;
        state.upi_qr_image = data?.list[0]?.QR_CODE_URL;
        state.upi_id = data?.list[0]?.UPI_ID;
        state.contact_person_name = data?.list[0]?.contact_person_name;
        state.pin_data =
          data?.list[0]?.PINNUMBER == 0 ||
          data?.list[0]?.PINNUMBER == "" ||
          data?.list[0]?.PINNUMBER == null
            ? 0
            : 1;
        state.user_turnover = data?.list[0]?.turnover;
        state.token_status = data?.list[0]?.TOKEN_STATUS;
      } else {
        state.profile_data = [];
        state.showroom_address_list = [];
        state.godown_address_list = [];
      }
    });
    builder.addCase(get_profile_data.pending, (state, action) => {
      state.profile_loading = true;
    });
    builder.addCase(get_profile_data.rejected, (state, action) => {
      state.profile_loading = false;
    });

    builder.addCase(get_emp_profile_data.fulfilled, (state, action) => {
      const data = action.payload;
      state.profile_loading = false;
      if (data.success) {
        state.emp_profile_data = data?.list;
        state.emp_img_url = data?.list?.IMAGE_URL || "";
        state.emp_pin_data = data?.list?.PIN_STATUS || "";
        // state.showroom_address_list =
        //   data?.list?.addresslist?.filter((v) => v?.ADDRESS_TYPE == "1") || [];
        // state.godown_address_list =
        //   data?.list?.addresslist?.filter((v) => v?.ADDRESS_TYPE == "2") || [];
        // state.img_url = data?.list[0]?.image_url;
        // state.seal_image = data?.list[0]?.seal_url;
        // state.signature_image = data?.list[0]?.sign_url;
        // state.upi_qr_image = data?.list[0]?.QR_CODE_URL;
        // state.upi_id = data?.list[0]?.UPI_ID;
        // state.contact_person_name = data?.list[0]?.contact_person_name;
        // state.pin_data =
        //   data?.list[0]?.PINNUMBER == 0 ||
        //   data?.list[0]?.PINNUMBER == "" ||
        //   data?.list[0]?.PINNUMBER == null
        //     ? 0
        //     : 1;
        // state.user_turnover = data?.list[0]?.turnover;
        // state.token_status = data?.list[0]?.TOKEN_STATUS;
      } else {
        state.emp_profile_data = {};
      }
    });
    builder.addCase(get_emp_profile_data.pending, (state, action) => {
      state.profile_loading = true;
    });
    builder.addCase(get_emp_profile_data.rejected, (state, action) => {
      state.profile_loading = false;
    });

    builder
      // Pending state - when request starts
      .addCase(get_dashboard_data.pending, (state) => {
        state.dashboard_loading = true;
        state.dashboard_error = null;
      })
      // Fulfilled state - when request succeeds
      .addCase(get_dashboard_data.fulfilled, (state, action) => {
        state.dashboard_loading = false;
        state.dashboard_error = null;

        const data = action.payload;

        if (data?.success) {
          state.dashboard_data = data?.data;
        } else {
          state.dashboard_data = null;
          state.dashboard_error =
            data?.message || "Failed to load dashboard data";
        }
      })
      // Rejected state - when request fails
      .addCase(get_dashboard_data.rejected, (state, action) => {
        state.dashboard_loading = false;
        state.dashboard_data = null;
        state.dashboard_error =
          action.payload || "Failed to fetch dashboard data";
      });

    builder.addCase(get_home_data.fulfilled, (state, action) => {
      const data = action.payload;

      state.home_data = data;
      state.day_end_date = data.effective_date
        ? moment(data.effective_date).format("DD/MM/YYYY")
        : "";
      state.alert_count = data?.alert_count;
      // if (data?.employee?.length > 0) {
      //   state.emp_data = data?.employee?.[0];
      // } else {
      //   state.emp_data = {};
      // }
    });

    // For Name
    builder.addCase(get_profile_name.fulfilled, (state, action) => {
      const data = action.payload;
      state.firm_name = data;
    });

    // For Name
    builder.addCase(get_profile_img_url.fulfilled, (state, action) => {
      const data = action.payload;
      state.img_url = data;
    });

    // For Pin
    builder.addCase(get_pin_data.fulfilled, (state, action) => {
      const data = action.payload;
      state.pin_data = data;
    });
    builder
      .addCase(get_purchase_sales.pending, (state) => {
        state.chart_loading.purchase_sales = true;
      })
      .addCase(get_purchase_sales.fulfilled, (state, action) => {
        state.chart_loading.purchase_sales = false;
        state.purchase_sales_data = action.payload;
      })

      .addCase(get_ncdbcrctv.pending, (state) => {
        state.chart_loading.ncdbcrctv = true;
      })
      .addCase(get_ncdbcrctv.fulfilled, (state, action) => {
        state.chart_loading.ncdbcrctv = false;
        state.ncdbcrctv_data = action.payload;
      })

      .addCase(get_sales.pending, (state) => {
        state.chart_loading.sales = true;
      })
      .addCase(get_sales.fulfilled, (state, action) => {
        state.chart_loading.sales = false;
        state.sales_data = action.payload;
      })
      .addCase(get_cashcredit.pending, (state) => {
        state.chart_loading.cashcredit = true;
      })
      .addCase(get_cashcredit.fulfilled, (state, action) => {
        state.chart_loading.cashcredit = false;
        state.cashcredit_data = action.payload;
      })

      .addCase(get_purchase_sales_expenses.pending, (state) => {
        state.chart_loading.purchase_sales_expenses = true;
      })
      .addCase(get_purchase_sales_expenses.fulfilled, (state, action) => {
        state.chart_loading.purchase_sales_expenses = false;
        state.purchase_sales_expenses_data = action.payload;
      });
  },
});

export default profileSlice.reducer;
